import { useEffect, useMemo, useState } from "react";
import { api, gbp, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { TrendingUp, AlertCircle, CheckCircle2, Layers, Download, Search, Truck, User as UserIcon, Building2, ChevronsUpDown } from "lucide-react";

function buildMonthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    out.push({ key, label });
  }
  return out;
}

const COLORS = { paid: "#0f766e", unpaid: "#b45309", void: "#475569" };

export default function Reports() {
  const months = useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(months[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/monthly-summary", { params: { month } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [month]);

  const po = data?.purchase_orders;
  const sb = data?.self_bills;
  const breakdownPie = po
    ? [
        { name: "Paid", value: po.paid, key: "paid" },
        { name: "Unpaid", value: po.unpaid, key: "unpaid" },
        { name: "Void", value: po.void, key: "void" },
      ].filter((s) => s.value > 0)
    : [];

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reporting</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">
            Monthly Summary
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Per-supplier outstanding totals and paid / unpaid breakdown for the selected month.
          </p>
        </div>
        <div className="w-full md:w-64">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="reports-month" className="rounded-sm border-slate-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Tile icon={Layers} label="POs this month" value={loading ? "—" : (po?.count ?? 0)} testid="kpi-po-count" />
        <Tile icon={TrendingUp} label="Total billed (POs)" value={loading ? "—" : gbp(po?.total ?? 0)} testid="kpi-po-total" />
        <Tile
          icon={CheckCircle2}
          label="Paid"
          value={loading ? "—" : gbp(po?.paid ?? 0)}
          tone="emerald"
          testid="kpi-paid"
        />
        <Tile
          icon={AlertCircle}
          label="Outstanding"
          value={loading ? "—" : gbp(po?.unpaid ?? 0)}
          tone="amber"
          testid="kpi-unpaid"
        />
      </div>

      {/* Per-supplier table + Paid vs Unpaid donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-sm border border-slate-200 shadow-none lg:col-span-2">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-display text-base font-medium text-slate-900">By Supplier</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full po-table text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Supplier</th>
                  <th className="px-4 py-3 font-semibold text-right">POs</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-right">Paid</th>
                  <th className="px-4 py-3 font-semibold text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody className="text-slate-900">
                {loading && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && (po?.by_supplier || []).length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">No POs in this month yet.</td></tr>
                )}
                {!loading && (po?.by_supplier || []).map((s) => (
                  <tr key={s.supplier} className="border-t border-slate-100" data-testid={`supplier-row-${s.supplier}`}>
                    <td className="px-4 py-3 font-medium">{s.supplier}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{gbp(s.total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{gbp(s.paid)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-amber-700">{gbp(s.unpaid)}</td>
                  </tr>
                ))}
              </tbody>
              {!loading && (po?.by_supplier || []).length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td className="px-4 py-3 text-xs uppercase tracking-wider font-semibold text-slate-600">Totals</td>
                    <td className="px-4 py-3 text-right tabular-nums font-display font-semibold">{po.count}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-display font-semibold">{gbp(po.total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-display font-semibold text-emerald-700">{gbp(po.paid)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-display font-semibold text-amber-700">{gbp(po.unpaid)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <h2 className="font-display text-base font-medium text-slate-900">Paid vs Outstanding</h2>
          <div className="h-72 mt-4" data-testid="paid-unpaid-donut">
            {breakdownPie.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No data to display
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={breakdownPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {breakdownPie.map((entry) => (
                      <Cell key={entry.key} fill={COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => gbp(v)} />
                  <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Self-bills per driver */}
      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-base font-medium text-slate-900">Self-Bills by Driver</h2>
          <div className="text-xs text-slate-500">
            {sb ? `${sb.count} bill${sb.count === 1 ? "" : "s"} · total ${gbp(sb.total)}` : "—"}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-slate-100">
          <div className="overflow-x-auto">
            <table className="w-full po-table text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Driver</th>
                  <th className="px-4 py-3 font-semibold">Driver ID</th>
                  <th className="px-4 py-3 font-semibold text-right">Bills</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="4" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && (sb?.by_driver || []).length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-10 text-center text-slate-500">No self-bills in this month yet.</td></tr>
                )}
                {!loading && (sb?.by_driver || []).map((d) => (
                  <tr key={d.driver} className="border-t border-slate-100" data-testid={`driver-row-${d.driver}`}>
                    <td className="px-4 py-3 font-medium">{d.driver}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{d.driver_id || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{d.count}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{gbp(d.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-6">
            <div className="h-64">
              {sb?.by_driver?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sb.by_driver.slice(0, 8)} layout="vertical" margin={{ left: 28, right: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} />
                    <YAxis type="category" dataKey="driver" tick={{ fill: "#64748b", fontSize: 11 }} width={120} />
                    <Tooltip formatter={(v) => gbp(v)} />
                    <Bar dataKey="total" fill="#0f172a" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No data to chart
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Tile({ icon: Icon, label, value, hint, testid, tone = "slate" }) {
  const toneClasses = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-700 text-white",
    amber: "bg-amber-600 text-white",
  };
  return (
    <Card data-testid={testid} className="rounded-sm border border-slate-200 shadow-none p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
          <div className="mt-3 font-display text-2xl font-semibold text-slate-900 tabular-nums">{value}</div>
          {hint && <div className="mt-2 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${toneClasses[tone] || toneClasses.slate}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

const contactActionRenderer = (testidPrefix) => (c) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        data-testid={`${testidPrefix}-menu-${c.id}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        <Download className="w-3.5 h-3.5" /> PDF
        <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-40">
      <DropdownMenuItem asChild>
        <a
          data-testid={`${testidPrefix}-pdf-${c.id}`}
          href={fileUrl(`/reports/contacts/${c.id}/profile.pdf`)}
          download rel="noopener"
          className="cursor-pointer"
        >PDF</a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a
          data-testid={`${testidPrefix}-xlsx-${c.id}`}
          href={fileUrl(`/reports/contacts/${c.id}/profile.xlsx`)}
          download rel="noopener"
          className="cursor-pointer"
        >Excel</a>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export function ProfileList({ title, subtitle, icon: Icon, items, hrefFor, testidPrefix, emptyHint, renderAction }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((x) => (x.name || "").toLowerCase().includes(s) || (x.email || "").toLowerCase().includes(s));
  }, [items, q]);
  return (
    <Card className="rounded-sm border border-slate-200 shadow-none">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-sm font-medium text-slate-900">{title}</div>
            <div className="text-xs text-slate-500">{subtitle}</div>
          </div>
        </div>
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name…"
            className="pl-8 h-8 text-xs rounded-sm border-slate-300"
          />
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-slate-500">
          {q ? "No matches." : emptyHint || "Nothing here yet."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtered.map((it) => (
            <li key={it.id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`${testidPrefix}-row-${it.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{it.name || "—"}</div>
                {it.email && <div className="text-xs text-slate-500 truncate">{it.email}</div>}
              </div>
              {renderAction ? renderAction(it) : (
                <a
                  href={hrefFor(it)}
                  download rel="noopener"
                  data-testid={`${testidPrefix}-pdf-${it.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ProfileDownloads() {
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/contacts"), api.get("/clients")])
      .then(([c, cl]) => { setContacts(c.data || []); setClients(cl.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const subs = useMemo(() => contacts.filter((c) => c.type === "customer" && c.is_active !== false), [contacts]);
  const drivers = useMemo(() => contacts.filter((c) => c.type === "subcontractor" && c.is_active !== false), [contacts]);
  const activeClients = useMemo(() => clients.filter((c) => c.is_active !== false), [clients]);

  return (
    <section data-testid="profile-downloads">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reporting · Profiles</div>
        <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-1">Download profiles</h2>
        <p className="text-sm text-slate-600 mt-1">One-click PDF profile sheets — contact, address, bank details, pay/charge rates, and recent activity.</p>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ProfileList
            title="Subcontractors"
            subtitle={`${subs.length} active`}
            icon={Truck}
            items={subs}
            testidPrefix="prof-sub"
            hrefFor={(c) => fileUrl(`/reports/contacts/${c.id}/profile.pdf`)}
            emptyHint="No active subcontractors."
            renderAction={contactActionRenderer("prof-sub")}
          />
          <ProfileList
            title="Self-employed drivers"
            subtitle={`${drivers.length} active`}
            icon={UserIcon}
            items={drivers}
            testidPrefix="prof-drv"
            hrefFor={(c) => fileUrl(`/reports/contacts/${c.id}/profile.pdf`)}
            emptyHint="No active drivers."
            renderAction={contactActionRenderer("prof-drv")}
          />
          <ProfileList
            title="Clients"
            subtitle={`${activeClients.length} active`}
            icon={Building2}
            items={activeClients}
            testidPrefix="prof-cli"
            hrefFor={(c) => fileUrl(`/reports/clients/${c.id}/profile.pdf`)}
            emptyHint="No active clients."
            renderAction={(c) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`prof-cli-menu-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                    <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                    Client profile
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a
                      data-testid={`prof-cli-pdf-${c.id}`}
                      href={fileUrl(`/reports/clients/${c.id}/profile.pdf`)}
                      download
                      rel="noopener"
                      className="cursor-pointer"
                    >
                      PDF — Standard (no pay rates)
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      data-testid={`prof-cli-pdf-payable-${c.id}`}
                      href={fileUrl(`/reports/clients/${c.id}/profile.pdf?include_payable=true`)}
                      download
                      rel="noopener"
                      className="cursor-pointer"
                    >
                      PDF — Include payable rates
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a
                      data-testid={`prof-cli-xlsx-${c.id}`}
                      href={fileUrl(`/reports/clients/${c.id}/profile.xlsx`)}
                      download
                      rel="noopener"
                      className="cursor-pointer"
                    >
                      Excel — Standard
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      data-testid={`prof-cli-xlsx-payable-${c.id}`}
                      href={fileUrl(`/reports/clients/${c.id}/profile.xlsx?include_payable=true`)}
                      download
                      rel="noopener"
                      className="cursor-pointer"
                    >
                      Excel — Include payable rates
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          />
        </div>
      )}
    </section>
  );
}
